import { Injectable } from "@angular/core";
import { SampleAdvancedSearchCriterias } from "@sharedcommon/model/sample-advanced-search-criterias.model";
import { SampleFilterDto } from "@sharedcommon/model/sample/filter/sample-filter.model";
import { FunctionUtils } from "@sharedcommon/util/functions.util";
import { BehaviorSubject } from "rxjs";

/**
 * Service which handle the advanced search for user part.
 * Put it in the common because the admin et user shared the same
 */
@Injectable()
export class SampleAdvancedSearchService {
  currentAdvancedSearch: BehaviorSubject<SampleAdvancedSearchCriterias|null>;

  constructor() {
    this.currentAdvancedSearch = new BehaviorSubject<SampleAdvancedSearchCriterias|null>(null);
  }

  /**
   * Return the advanced search subject
   * @returns the subject where to subscribe
   */
  getCurrentAdvancedSearch(): BehaviorSubject<SampleAdvancedSearchCriterias|null> {
    return this.currentAdvancedSearch;
  }

  /**
   * Return the current value of the advanced search
   */
  returnCurrentAdvancedSearchValue(): SampleAdvancedSearchCriterias|null {
    return this.currentAdvancedSearch.value;
  }

  /**
   * Will update the subject and store the value in the localStorage
   * @param advancedSearchCriterias
   */
  setCurrentAdvancedSearch(advancedSearchCriterias: SampleAdvancedSearchCriterias): void {
    this.currentAdvancedSearch.next(advancedSearchCriterias);
  }

  /**
   * Reset the stored advanced search and delete it from the localStorage
   */
  resetAdvancedSearch(): void {
    this.currentAdvancedSearch.complete();
    this.currentAdvancedSearch = new BehaviorSubject<SampleAdvancedSearchCriterias|null>(null);
  }

  /**
   * Fill the sample filter DTO according the criterias for Sample meta datas (farm, technician, distributor)
   * @param filterDto the filter DTO to fill
   * @param criterias the criterias which might contains the data to fill
   */
  static fillSampleFilterAccordingCriteriasForSampleMetaData(filterDto: SampleFilterDto, criterias: SampleAdvancedSearchCriterias): void {
    if (criterias) {
      filterDto.inBusinessUnitCodes = (criterias.businessUnits ?? []).map(item => item.code);
      filterDto.inEntityCodes = (criterias.entities ?? []).map(item => item.code);
      filterDto.inSupplierCodes = (criterias.suppliers ?? []).map(item => item.code);
      filterDto.inSpeciesIds = (criterias.allSpecies ?? []).map(item => item.id);
      filterDto.inReferenceCodes = (criterias.references ?? []).map(item => item.code);
      /* Nested if condition to to test from the nearest field from Reference to the furthest fields in terms of left joins */
      if (filterDto.inReferenceCodes.length === 0) {
        filterDto.inPredictionModelCodes = (criterias.predictionModels ?? []).map(item => item.code);
        if (filterDto.inPredictionModelCodes.length === 0) {
          filterDto.inLevelCodes = (criterias.filteredLevels ?? []).map(item => item.code);
        }
      }
    }
  }


  /**
   * Fill the sample filter DTO according the criterias for Sample itself
   * @param filterDto     the filter DTO to fill
   * @param criterias     the criterias which might contains the data to fill
   * @param withSampleIds at true if you want the filter on sample IDs
   */
  static fillSampleFilterAccordingCriteriasForSample(
    filterDto: SampleFilterDto,
    criterias: SampleAdvancedSearchCriterias,
    withSampleIds: boolean
  ): void {
    if (criterias) {
      if (withSampleIds && criterias.samples?.length > 0) {
        /* Filter on sample ids is predominating on all others criterias
         * that is why there is a return just below it */
        filterDto.ids = withSampleIds ? (criterias.samples ?? []).map(item => item.id) : [];
        return;
      }
      this.fillSampleFilterAccordingCriteriasForSampleMetaData(filterDto, criterias);
      filterDto.inTechnicianNames = criterias.technicianNames ?? [];
      filterDto.inDistributors = criterias.distributorNames ?? [];
      filterDto.inFarmLabels = (criterias.farmNames ?? []);
      if (criterias.endValidationDate) {
        filterDto.inEndValidation = FunctionUtils.convertDateToIsoFormat(criterias.endValidationDate);
      }
      if (criterias.startValidationDate) {
        filterDto.inStartValidation = FunctionUtils.convertDateToIsoFormat(criterias.startValidationDate);
      }
      if (criterias.endReceptionDate) {
        filterDto.inEndReception = FunctionUtils.convertDateToIsoFormat(criterias.endReceptionDate);
      }
      if (criterias.startReceptionDate) {
        filterDto.inStartReception = FunctionUtils.convertDateToIsoFormat(criterias.startReceptionDate);
      }
    }
  }
}
